import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, TextField,
  Select, MenuItem, Switch, Button, IconButton, Chip, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import blinkscribeapi from "../api/blinkscribeapi";
import { holdsScreen } from "../config/screenPrivileges";

/* Practice vocabulary (bsfix-name-vocab / hubfix-practice-vocab, 2026-10-19).
   The names and terms BlinkScribe spells exactly in transcripts and notes:
   - read-only names that come from the chart (active providers, the
     practice's BlinkScribe users, referral physicians) — they change when
     those records change;
   - the practice's custom terms (names, drugs, clinics, other), with
     optional "often heard as" spellings. Practice admins edit them (the
     server checks: user role 6/7/8/9 or the Side Bar "Vocabulary" or
     "Users" privilege).
   Spelling reference only: never patient names. Changes apply to the next
   recordings (within about 10 minutes). */

const KINDS = [
  ["name", "Name"],
  ["drug", "Drug"],
  ["clinic", "Clinic / place"],
  ["other", "Other term"],
];
const SOURCE_LABELS = [
  ["provider", "Providers"],
  ["user", "BlinkScribe users"],
  ["referral", "Referral physicians"],
];
const EMPTY = { id: null, term: "", kind: "name", aliases: "", enabled: true };

// Who sees the menu entry: the Side Bar "Vocabulary" privilege (split off
// "Users" 11-03), the "Users" privilege or an admin user role (IT-Admin,
// IT-Development, Practice-Admin, IT-QA) - the rule the BlinkScribe save
// route enforces.
export const VOCAB_ADMIN_ROLES = ["6", "7", "8", "9"];
export const isVocabularyAdmin = (sidebarPrivileges, user) =>
  holdsScreen(sidebarPrivileges, "vocabulary") ||
  (user?.grole !== undefined && user?.grole !== null && VOCAB_ADMIN_ROLES.includes(String(user.grole).trim()));

const errorText = (e, fallback) => e?.response?.data?.message || fallback;

export default function PracticeVocabularyModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await blinkscribeapi.get("/v1/vocabulary/list");
      setData(res?.data ?? null);
    } catch (e) {
      setMsg(errorText(e, "Could not load the practice vocabulary"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSel(EMPTY);
    setMsg("");
    load();
  }, [open]);

  const canEdit = !!data?.can_edit;
  const custom = Array.isArray(data?.custom) ? data.custom : [];
  const auto = Array.isArray(data?.auto) ? data.auto : [];

  const save = async (row) => {
    if (!row.term?.trim()) {
      setMsg("Enter a term");
      return;
    }
    setSaving(true);
    setMsg("");
    try {
      await blinkscribeapi.post("/v1/vocabulary/save", {
        id: row.id || undefined,
        term: row.term.trim(),
        kind: row.kind,
        aliases: row.aliases || "",
        enabled: !!row.enabled,
      });
      setMsg("Saved");
      setSel(EMPTY);
      await load();
    } catch (e) {
      setMsg(errorText(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Delete "${row.term}" from the practice vocabulary?`)) return;
    setSaving(true);
    setMsg("");
    try {
      await blinkscribeapi.post("/v1/vocabulary/delete", { id: row.id });
      if (sel.id === row.id) setSel(EMPTY);
      await load();
    } catch (e) {
      setMsg(errorText(e, "Delete failed"));
    } finally {
      setSaving(false);
    }
  };

  // fix_mode: off | suggest (proposals recorded, nothing changed) | apply; older servers sent only fix_enabled
  const fixMode = data ? (data.fix_mode || (data.fix_enabled ? "apply" : "off")) : "off";
  const FIX_TEXT = { off: "off", suggest: "suggestions only", apply: "on" };
  const hintPaths = Array.isArray(data?.hint_paths) ? data.hint_paths : (data?.hint_enabled ? ["on"] : []);
  const status = data
    ? `Spelling correction ${FIX_TEXT[fixMode] || "off"} · prompt hints ${hintPaths.length ? hintPaths.join(", ") : "off"} for this practice`
    : "";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: "flex", alignItems: "center", fontWeight: 600 }}>
        Practice vocabulary
        <Typography sx={{ ml: 1.5, fontSize: 13, color: "#6B7280", flex: 1 }}>
          Names and terms BlinkScribe spells exactly in transcripts and notes. Spelling reference only — never add patient names.
        </Typography>
        <IconButton aria-label="Close" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", gap: 2, minHeight: 460, flexWrap: { xs: "wrap", md: "nowrap" } }}>
        {loading && !data ? (
          <Box sx={{ m: "auto" }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            {/* From the chart (read-only) */}
            <Box sx={{ width: { xs: "100%", md: 380 }, borderRight: { md: "1px solid #E5E7EB" }, pr: { md: 2 }, overflowY: "auto" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>From the chart</Typography>
              <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
                Read live from provider, user and referral records. Change those records to change these names.
              </Typography>
              {SOURCE_LABELS.map(([source, label]) => {
                const items = auto.filter((e) => e.source === source);
                return (
                  <Box key={source} sx={{ mb: 1.5 }} data-testid={`vocab-auto-${source}`}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", mb: 0.5 }}>
                      {label} ({items.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {items.map((e) => (
                        <Chip key={`${e.source}:${e.name}`} size="small" variant="outlined"
                          label={e.specialty ? `${e.name} — ${e.specialty}` : e.name}
                          title={[e.specialty, e.clinic].filter(Boolean).join(" · ") || undefined} />
                      ))}
                      {items.length === 0 && <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>None</Typography>}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Custom terms */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                  Practice terms ({custom.length}{data?.max_terms ? ` of ${data.max_terms}` : ""})
                </Typography>
                {canEdit && (
                  <Button size="small" variant="outlined" sx={{ textTransform: "none" }}
                    onClick={() => { setSel(EMPTY); setMsg(""); }}>
                    + New term
                  </Button>
                )}
              </Box>
              {data && data.table_ready === false && (
                <Typography sx={{ fontSize: 12, color: "#B45309", mb: 1 }}>
                  The practice terms list is not set up on the server yet.
                </Typography>
              )}
              {data && !canEdit && data.table_ready !== false && (
                <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
                  Only a practice administrator can change these terms.
                </Typography>
              )}
              <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 1, maxHeight: 260, overflowY: "auto", mb: 2 }}>
                {custom.length === 0 && (
                  <Typography sx={{ fontSize: 12, color: "#9CA3AF", p: 1.5 }}>No practice terms yet.</Typography>
                )}
                {custom.map((row) => (
                  <Box key={row.id} data-testid="vocab-custom-row"
                    sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, borderBottom: "1px solid #F3F4F6",
                          bgcolor: sel.id === row.id ? "#EEF2FB" : "transparent" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: row.enabled ? "#111827" : "#9CA3AF" }}>
                        {row.term}
                        <Typography component="span" sx={{ fontSize: 11, color: "#6B7280", ml: 1 }}>
                          {(KINDS.find(([k]) => k === row.kind) || [null, row.kind])[1]}
                        </Typography>
                      </Typography>
                      {row.aliases && (
                        <Typography sx={{ fontSize: 11, color: "#6B7280" }} noWrap title={row.aliases}>
                          Often heard as: {row.aliases}
                        </Typography>
                      )}
                    </Box>
                    {canEdit && (
                      <>
                        <Switch size="small" checked={!!row.enabled} disabled={saving}
                          inputProps={{ "aria-label": `Use ${row.term}` }}
                          onChange={() => save({ ...row, enabled: !row.enabled })} />
                        <Button size="small" sx={{ textTransform: "none", minWidth: 0 }}
                          onClick={() => { setSel({ ...row }); setMsg(""); }}>Edit</Button>
                        <Button size="small" color="error" sx={{ textTransform: "none", minWidth: 0 }}
                          disabled={saving} onClick={() => remove(row)}>Delete</Button>
                      </>
                    )}
                  </Box>
                ))}
              </Box>

              {canEdit && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }} data-testid="vocab-editor">
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{sel.id ? "Edit term" : "Add a term"}</Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <TextField size="small" label="Term (spelled exactly)" value={sel.term} sx={{ flex: 1, minWidth: 220 }}
                      inputProps={{ maxLength: 120 }}
                      onChange={(e) => setSel({ ...sel, term: e.target.value })} />
                    <Select size="small" value={sel.kind} sx={{ minWidth: 150 }}
                      inputProps={{ "aria-label": "Kind" }}
                      onChange={(e) => setSel({ ...sel, kind: e.target.value })}>
                      {KINDS.map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
                    </Select>
                  </Box>
                  <TextField size="small" label="Often heard as (optional, comma separated)" value={sel.aliases || ""}
                    helperText="Misspellings to always replace with this term, e.g. Madu, Mathu. Common words are refused."
                    inputProps={{ maxLength: 500 }}
                    onChange={(e) => setSel({ ...sel, aliases: e.target.value })} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Switch size="small" checked={!!sel.enabled} inputProps={{ "aria-label": "Use this term" }}
                      onChange={(e) => setSel({ ...sel, enabled: e.target.checked })} />
                    <Typography sx={{ fontSize: 13, flex: 1 }}>Use this term</Typography>
                    {sel.id && (
                      <Button size="small" sx={{ textTransform: "none" }} onClick={() => setSel(EMPTY)}>Cancel</Button>
                    )}
                    <Button size="small" variant="contained" disabled={saving} sx={{ textTransform: "none" }}
                      onClick={() => save(sel)}>
                      {saving ? "Saving…" : "Save"}
                    </Button>
                  </Box>
                </Box>
              )}
              {msg && <Typography role="status" sx={{ fontSize: 12, color: msg === "Saved" ? "#047857" : "#B91C1C", mt: 1 }}>{msg}</Typography>}
              {status && <Typography sx={{ fontSize: 11, color: "#9CA3AF", mt: 2 }}>{status}</Typography>}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
